import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllShippingCompanies,
  createShippingCompany,
  updateShippingCompany,
  deleteShippingCompany,
  getAllCountries,
  getAllWarehouses,
} from "../../redux/actions";
import { LoadingModal } from "../../components/modal/loading";
import {
  CreateShippingCompanyModal,
  EditShippingCompanyModal,
  DeleteConfirmModal,
} from "../../components/shipping/ShippingModals";
import { ShippingCompanyDetailModal } from "../../components/shipping/DetailModals";

export const ShippingCompany = () => {
  const dispatch = useDispatch();
  const { companies, countries, warehouses, loading } = useSelector(
    (s) => s.shipping,
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    dispatch(getAllShippingCompanies());
    dispatch(getAllCountries());
    dispatch(getAllWarehouses());
  }, [dispatch]);

  const handleCreate = (form) => {
    dispatch(createShippingCompany(form));
    setCreateOpen(false);
  };

  const handleUpdate = (form) => {
    dispatch(updateShippingCompany(selected.id, form));
    setEditOpen(false);
    setSelected(null);
  };

  const handleDelete = () => {
    dispatch(deleteShippingCompany(selected.id));
    setDeleteOpen(false);
    setSelected(null);
  };

  const typeColor = {
    AIR: "bg-sky-50 text-sky-700",
    SEA: "bg-teal-50 text-teal-700",
    LAND: "bg-orange-50 text-orange-700",
  };
  const modeColor = {
    EXPRESS: "bg-emerald-50 text-emerald-700",
    STANDARD: "bg-blue-50 text-blue-700",
    ECONOMY: "bg-slate-100 text-slate-600",
  };

  return (
    <div className="space-y-6">
      {loading && <LoadingModal text="Loading..." />}

      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">
          Shipping Companies
        </h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
        >
          Add Company
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                #
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Name
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Type
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Mode
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Country
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Delivery
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                Agent WH
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {companies.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-slate-400 text-sm"
                >
                  No shipping companies found. Add one to get started.
                </td>
              </tr>
            ) : (
              companies.map((c, i) => (
                <tr key={c.id} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                  <td className="px-3 py-3 font-medium text-slate-800">
                    {c.name}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-lg px-2 py-1 text-xs font-semibold ${typeColor[c.type] || "bg-slate-100 text-slate-600"}`}
                    >
                      {c.type}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-lg px-2 py-1 text-xs font-semibold ${modeColor[c.mode] || "bg-slate-100 text-slate-600"}`}
                    >
                      {c.mode}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    {c.country?.name}
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    {c.deliveryTimeMin}–{c.deliveryTimeMax} days
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    {c.agentWarehouse?.name}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => {
                        setSelected(c);
                        setDetailOpen(true);
                      }}
                      className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      View
                    </button>
                    <button
                      onClick={() => {
                        setSelected(c);
                        setEditOpen(true);
                      }}
                      className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelected(c);
                        setDeleteOpen(true);
                      }}
                      className="rounded-lg bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-100"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateShippingCompanyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        countries={countries}
        warehouses={warehouses}
      />
      <EditShippingCompanyModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        onUpdate={handleUpdate}
        company={selected}
        countries={countries}
        warehouses={warehouses}
      />
      <ShippingCompanyDetailModal
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelected(null);
        }}
        company={selected}
      />
      <DeleteConfirmModal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
        title="Delete Shipping Company"
        message={`Are you sure you want to delete "${selected?.name}"? This will also remove all associated prices and rates.`}
      />
    </div>
  );
};
